import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Title } from 'react-admin';
import {
  AgingBucket,
  ClaimStatus,
  EhrClaim,
  EhrClaimsMetricsResponse,
  ErrorSummaryItem,
  PaginationInfo,
} from '../../interfaces/billing';
import { SelectOption } from '../../interfaces/common';
import { getClinicsForSelect } from '../../lib/clinic';
import { BillingService } from '../../services';
import { CompactTable } from '../common/CompactTable/CompactTable';
import { CustomTabs } from '../common/CustomTabs/CustomTabs';
import { Loader } from '../common/Loader';
import { SectionTitle } from '../common/SectionTitle';
import { TabPanel } from '../common/TabPanel/TabPanel';
import { ClaimsMetrics } from './ClaimsMetrics';

const STATUS_CHIP_COLORS: Record<string, 'warning' | 'primary' | 'error' | 'success' | 'default'> =
  {
    [ClaimStatus.Pending]: 'warning',
    [ClaimStatus.Uploaded]: 'primary',
    [ClaimStatus.UploadedWithErrors]: 'error',
    [ClaimStatus.Processed]: 'success',
  };

const STATUS_LABELS: Record<string, string> = {
  [ClaimStatus.Pending]: 'Pending',
  [ClaimStatus.Uploaded]: 'Uploaded',
  [ClaimStatus.UploadedWithErrors]: 'With Errors',
  [ClaimStatus.Processed]: 'Processed',
};

const CLAIMS_COLUMNS = [
  { key: 'id', label: 'ID', width: 80 },
  { key: 'patient', label: 'Patient' },
  { key: 'serviceDate', label: 'Service Date', width: 120 },
  { key: 'source', label: 'Source', width: 100 },
  { key: 'status', label: 'Status', width: 120 },
  { key: 'action', label: 'Action', align: 'center' as const, width: 80 },
];

const ERROR_SUMMARY_COLUMNS = [
  { key: 'type', label: 'Type', width: 100 },
  { key: 'description', label: 'Description' },
  { key: 'count', label: 'Claims', align: 'right' as const, width: 100 },
  { key: 'action', label: 'Action', align: 'center' as const, width: 80 },
];

const ERROR_CLAIMS_COLUMNS = [
  { key: 'id', label: 'Claim ID', width: 100 },
  { key: 'serviceDate', label: 'Service Date' },
  { key: 'action', label: 'Action', align: 'center' as const, width: 80 },
];

const ClaimOpenLink: React.FC<{ claimId: number }> = ({ claimId }) => {
  return (
    <Link
      href={`#/claims/${claimId}/show`}
      target='_blank'
      rel='noopener'
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      Open <OpenInNewIcon sx={{ fontSize: 16 }} />
    </Link>
  );
};

const ClinicSelect: React.FC<{
  clinicId: string;
  clinicOptions: SelectOption[];
  onChange: (value: string) => void;
}> = ({ clinicId, clinicOptions, onChange }) => {
  return (
    <FormControl fullWidth size='small'>
      <InputLabel>Clinic</InputLabel>
      <Select
        value={clinicId}
        label='Clinic'
        onChange={(e) => onChange(e.target.value as string)}>
        {clinicOptions.map((clinic) => {
          return (
            <MenuItem key={clinic.value} value={clinic.value}>
              {clinic.label}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export const ClaimsMetricsPage: React.FC = () => {
  const [clinicOptions, setClinicOptions] = useState<SelectOption[]>([]);
  const [clinicId, setClinicId] = useState<string>('');
  const [fromDate, setFromDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [untilDate, setUntilDate] = useState(moment().format('YYYY-MM-DD'));
  const [metrics, setMetrics] = useState<EhrClaimsMetricsResponse['metrics'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [clinicsLoading, setClinicsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [claims, setClaims] = useState<EhrClaim[]>([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [claimsPagination, setClaimsPagination] = useState<PaginationInfo | null>(null);
  const [errorSummary, setErrorSummary] = useState<ErrorSummaryItem[]>([]);
  const [selectedError, setSelectedError] = useState<ErrorSummaryItem | null>(null);
  const [agingBuckets, setAgingBuckets] = useState<AgingBucket[]>([]);
  const [agingTotal, setAgingTotal] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [showAllClaims, setShowAllClaims] = useState(false);
  const [issuesLoading, setIssuesLoading] = useState(false);

  useEffect(() => {
    const loadClinics = async () => {
      setClinicsLoading(true);
      try {
        const clinics = await getClinicsForSelect();
        const filtered = clinics.filter(
          (c) => c.apero_config?.apero_claims_sync_status === 'enabled',
        );
        const options = filtered.map((c) => {
          return { value: `${c.id}`, label: c.name };
        });
        setClinicOptions(options);
        if (options.length > 0) {
          setClinicId(options[0].value);
        }
      } catch (error) {
        console.error('Failed to load clinics:', error);
      } finally {
        setClinicsLoading(false);
      }
    };
    loadClinics();
  }, []);

  const fetchOverview = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    setSelectedStatus(null);
    setClaims([]);
    try {
      const response = await new BillingService().getClaimsMetrics({
        fromDate,
        untilDate,
        clinicIds: clinicId,
      });
      if (response?.success) {
        setMetrics(response.metrics);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  }, [clinicId, fromDate, untilDate]);

  const fetchIssues = useCallback(async () => {
    if (!clinicId) return;
    setIssuesLoading(true);
    setSelectedError(null);
    try {
      const billingService = new BillingService();
      const [errorsResponse, agingResponse] = await Promise.all([
        billingService.getClaimsErrorSummary({ clinicIds: clinicId }),
        billingService.getClaimsAging({ clinicIds: clinicId }),
      ]);
      if (errorsResponse?.success) {
        setErrorSummary(errorsResponse.errors);
      }
      if (agingResponse?.success) {
        setAgingBuckets(agingResponse.buckets);
        setAgingTotal(agingResponse.total);
      }
    } catch (error) {
      console.error('Error fetching issues:', error);
    } finally {
      setIssuesLoading(false);
    }
  }, [clinicId]);

  const fetchClaimsByStatus = async (status: string | null, page: number = 1) => {
    if (!clinicId) return;
    setClaimsLoading(true);
    setSelectedError(null);
    try {
      const response = await new BillingService().getClaims({
        fromDate,
        untilDate,
        clinicIds: clinicId,
        ...(status && { statuses: status }),
        page,
        perPage: 50,
      });
      if (response?.success) {
        setClaims(response.claims);
        setClaimsPagination(response.pagination || null);
      }
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setClaimsLoading(false);
    }
  };

  const handleStatusClick = (status: string | null) => {
    if (selectedStatus === status) {
      setSelectedStatus(null);
      setClaims([]);
      return;
    }
    setSelectedStatus(status);
    fetchClaimsByStatus(status);
  };

  const handleErrorClick = (error: ErrorSummaryItem) => {
    if (selectedError === error) {
      setSelectedError(null);
      return;
    }
    setSelectedError(error);
    setSelectedStatus(null);
    setClaims([]);
    setShowAllClaims(false);
  };

  useEffect(() => {
    if (clinicId) {
      fetchOverview();
      fetchIssues();
    }
  }, [clinicId, fetchOverview, fetchIssues]);

  if (clinicsLoading) {
    return <Loader />;
  }

  // Row builders
  const buildClaimRows = (claimsList: EhrClaim[]) => {
    return claimsList.map((claim) => {
      return {
        id: claim.id,
        patient: claim.corePatient
          ? `${claim.corePatient.firstName} ${claim.corePatient.lastName}`
          : '-',
        serviceDate: moment.utc(claim.serviceDate).format('YYYY-MM-DD'),
        source: claim.source,
        status: (
          <Chip
            label={STATUS_LABELS[claim.status] || claim.status}
            color={STATUS_CHIP_COLORS[claim.status] || 'default'}
            size='small'
            variant='outlined'
          />
        ),
        action: <ClaimOpenLink claimId={claim.id} />,
      };
    });
  };

  const buildErrorSummaryRows = () => {
    return errorSummary.map((error) => {
      return {
        type: (
          <Chip
            label={error.category === 'provider' ? 'Provider' : 'Error'}
            color={error.category === 'provider' ? 'warning' : 'error'}
            size='small'
            variant='outlined'
          />
        ),
        description: error.description,
        count: <Chip label={error.count} color='error' size='small' variant='outlined' />,
        action: (
          <Typography variant='body2' color='primary'>
            View
          </Typography>
        ),
        onClick: () => {
          handleErrorClick(error);
        },
      };
    });
  };

  const buildErrorClaimRows = () => {
    if (!selectedError) {
      return [];
    }
    const items = showAllClaims ? selectedError.claims : selectedError.claims.slice(0, 10);
    return items.map((claim) => {
      return {
        id: `#${claim.id}`,
        serviceDate: moment.utc(claim.serviceDate).format('YYYY-MM-DD'),
        action: <ClaimOpenLink claimId={claim.id} />,
      };
    });
  };

  const tabLabels = [
    { label: 'Overview' },
    { label: agingTotal > 0 ? `Issues (${agingTotal})` : 'Issues' },
  ];

  const claimsTitle = `${STATUS_LABELS[selectedStatus || ''] || selectedStatus || ''} Claims${
    claimsPagination ? ` (${claimsPagination.totalCount})` : ''
  }`;

  const errorClaimsTitle =
    selectedError?.category === 'provider'
      ? `Claims for provider: "${selectedError?.description}" (${selectedError?.claims.length})`
      : `Claims with: "${selectedError?.description}" (${selectedError?.claims.length})`;

  return (
    <>
      <Title title='Claims Metrics' />
      <Card>
        <CardContent>
          <SectionTitle label='Claims Metrics' variant='h5' style={{ fontWeight: '500' }} />
          <Divider sx={{ mb: 2 }} />

          <CustomTabs
            tabLabels={tabLabels}
            value={activeTab}
            onChange={(_, v) => {
              setActiveTab(v);
            }}
          />

          {/* Tab 0: Overview */}
          <TabPanel value={activeTab} index={0} sx={{ p: 0, pt: 2 }}>
            {/* Filters */}
            <Grid container spacing={2} alignItems='center' sx={{ mb: 2 }}>
              <Grid item xs={12} sm={3}>
                <ClinicSelect
                  clinicId={clinicId}
                  clinicOptions={clinicOptions}
                  onChange={setClinicId}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label='From'
                  type='date'
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size='small'
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label='Until'
                  type='date'
                  value={untilDate}
                  onChange={(e) => {
                    setUntilDate(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size='small'
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={fetchOverview}
                    disabled={loading}>
                    Apply
                  </Button>
                  <IconButton onClick={fetchOverview} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>

            <ClaimsMetrics
              metrics={metrics}
              loading={loading}
              selectedStatus={selectedStatus}
              onStatusClick={handleStatusClick}
            />

            {/* Claims Table (from status click) */}
            {selectedStatus !== null && selectedStatus !== undefined && (
              <Box sx={{ mt: 3 }}>
                <Typography variant='h6' sx={{ mb: 1 }}>
                  {claimsTitle}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {claimsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <CompactTable
                    columns={CLAIMS_COLUMNS}
                    rows={buildClaimRows(claims)}
                    defaultText='No claims found.'
                    minWidth={500}
                    pagination={
                      claimsPagination && claimsPagination.totalPages > 1
                        ? {
                            rowsPerPageOptions: [50],
                            rowPerPage: claimsPagination.perPage,
                            page: claimsPagination.currentPage,
                            totalCount: claimsPagination.totalCount,
                            onPageChange: (page) => {
                              fetchClaimsByStatus(selectedStatus, page);
                            },
                          }
                        : undefined
                    }
                  />
                )}
              </Box>
            )}
          </TabPanel>

          {/* Tab 1: Issues */}
          <TabPanel value={activeTab} index={1} sx={{ p: 0, pt: 2 }}>
            <Grid container spacing={2} alignItems='center' sx={{ mb: 2 }}>
              <Grid item xs={12} sm={3}>
                <ClinicSelect
                  clinicId={clinicId}
                  clinicOptions={clinicOptions}
                  onChange={setClinicId}
                />
              </Grid>
              <Grid item>
                <IconButton onClick={fetchIssues} disabled={issuesLoading} size='small'>
                  <RefreshIcon />
                </IconButton>
              </Grid>
            </Grid>

            {issuesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Aging Claims */}
                {agingTotal > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant='h6' sx={{ mb: 1 }}>
                      Aging Unresolved Claims ({agingTotal})
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Grid container spacing={2}>
                      {agingBuckets
                        .filter((b) => {
                          return b.count > 0;
                        })
                        .map((bucket) => {
                          const isCritical = bucket.minDays >= 50;
                          const isHigh = bucket.minDays >= 30;
                          return (
                            <Grid item xs={6} sm={3} key={bucket.label}>
                              <Paper
                                variant='outlined'
                                sx={{
                                  p: 1.5,
                                  textAlign: 'center',
                                  borderColor: isCritical
                                    ? 'error.main'
                                    : isHigh
                                      ? 'warning.main'
                                      : undefined,
                                  borderWidth: isCritical || isHigh ? 2 : 1,
                                }}>
                                <Typography
                                  variant='h5'
                                  fontWeight={600}
                                  color={
                                    isCritical
                                      ? 'error.main'
                                      : isHigh
                                        ? 'warning.main'
                                        : 'text.primary'
                                  }>
                                  {bucket.count}
                                </Typography>
                                <Typography variant='caption' color='text.secondary'>
                                  {bucket.label}
                                </Typography>
                              </Paper>
                            </Grid>
                          );
                        })}
                    </Grid>
                  </Box>
                )}

                {/* Error Summary */}
                {errorSummary.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant='h6' sx={{ mb: 1 }}>
                      Error Summary
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <CompactTable
                      columns={ERROR_SUMMARY_COLUMNS}
                      rows={buildErrorSummaryRows()}
                      minWidth={400}
                    />
                  </Box>
                )}

                {/* Selected Error - Affected Claims */}
                {selectedError && (
                  <Box>
                    <Typography variant='h6' sx={{ mb: 1 }}>
                      {errorClaimsTitle}
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <CompactTable
                      columns={ERROR_CLAIMS_COLUMNS}
                      rows={buildErrorClaimRows()}
                      defaultText='No claims found.'
                      minWidth={300}
                    />
                    {!showAllClaims && selectedError.claims.length > 10 && (
                      <Button
                        size='small'
                        onClick={() => {
                          setShowAllClaims(true);
                        }}
                        sx={{ mt: 1 }}>
                        Show all {selectedError.claims.length} claims
                      </Button>
                    )}
                  </Box>
                )}

                {agingTotal === 0 && errorSummary.length === 0 && (
                  <Typography color='text.secondary' sx={{ py: 4, textAlign: 'center' }}>
                    No issues found.
                  </Typography>
                )}
              </>
            )}
          </TabPanel>
        </CardContent>
      </Card>
    </>
  );
};

export default ClaimsMetricsPage;
